'use client';

import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Sidebar from '@/components/common/Sidebar';
import Breadcrumb from '@/components/common/Breadcrumb';
import Icon from '@/components/ui/AppIcon';
import TaskFilters from './TaskFilters';
import TaskTable from './TaskTable';
import BulkActionToolbar from './BulkActionToolbar';
import TaskDetailsModal from './TaskDetailsModal';

interface Task {
  id: string;
  title: string;
  processName: string;
  assignee: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  dueDate: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue' | 'delegated';
  completionPercentage: number;
  slaHours: number;
  description?: string;
  createdDate?: string;
  lastUpdated?: string;
  comments?: Array<{ author: string; text: string; timestamp: string }>;
}

interface FilterOptions {
  status: string[];
  priority: string[];
  assignee: string[];
  dueDateRange: string;
  savedView: string;
}

const TaskManagementInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    status: [],
    priority: [],
    assignee: [],
    dueDateRange: 'all',
    savedView: '',
  });

  const mockTasks: Task[] = [
    {
      id: 'TSK-001',
      title: 'Aprovar Relatório Trimestral',
      processName: 'Aprovação de Relatórios',
      assignee: 'Maria Silva',
      priority: 'critical',
      dueDate: '08/12/2025',
      status: 'pending',
      completionPercentage: 0,
      slaHours: 2,
      description: 'Revisar e aprovar o relatório financeiro do terceiro trimestre de 2025.',
      createdDate: '05/12/2025 09:00',
      lastUpdated: '08/12/2025 10:30',
      comments: [
        { author: 'João Santos', text: 'Documento anexado para revisão.', timestamp: '06/12/2025 14:20' },
      ],
    },
    {
      id: 'TSK-002',
      title: 'Revisar Proposta de Orçamento',
      processName: 'Gestão de Orçamento',
      assignee: 'Carlos Oliveira',
      priority: 'high',
      dueDate: '10/12/2025',
      status: 'in_progress',
      completionPercentage: 45,
      slaHours: 48,
      description: 'Analisar a proposta de orçamento para o próximo ano fiscal.',
      createdDate: '03/12/2025 11:15',
      lastUpdated: '08/12/2025 16:45',
    },
    {
      id: 'TSK-003',
      title: 'Atualizar Documentação do Sistema',
      processName: 'Manutenção de Documentação',
      assignee: 'Ana Costa',
      priority: 'medium',
      dueDate: '15/12/2025',
      status: 'in_progress',
      completionPercentage: 60,
      slaHours: 120,
      description: 'Atualizar a documentação técnica do sistema BPM com as novas funcionalidades.',
    },
    {
      id: 'TSK-004',
      title: 'Processar Solicitação de Férias',
      processName: 'Gestão de RH',
      assignee: 'Pedro Almeida',
      priority: 'low',
      dueDate: '20/12/2025',
      status: 'pending',
      completionPercentage: 0,
      slaHours: 240,
      description: 'Processar e aprovar solicitações de férias para o período de fim de ano.',
    },
    {
      id: 'TSK-005',
      title: 'Revisar Contrato de Fornecedor',
      processName: 'Gestão de Contratos',
      assignee: 'Lucia Fernandes',
      priority: 'high',
      dueDate: '09/12/2025',
      status: 'overdue',
      completionPercentage: 20,
      slaHours: -12,
      description: 'Revisar os termos do contrato com o fornecedor principal antes da renovação.',
      comments: [
        { author: 'Roberto Lima', text: 'Urgente: prazo vencendo em breve.', timestamp: '07/12/2025 09:30' },
      ],
    },
    {
      id: 'TSK-006',
      title: 'Configurar Novo Fluxo de Trabalho',
      processName: 'Automação de Processos',
      assignee: 'Fernando Souza',
      priority: 'medium',
      dueDate: '12/12/2025',
      status: 'in_progress',
      completionPercentage: 75,
      slaHours: 72,
      description: 'Configurar e testar o novo fluxo de trabalho para aprovação de despesas.',
    },
    {
      id: 'TSK-007',
      title: 'Validar Dados de Integração',
      processName: 'Integração de Sistemas',
      assignee: 'Juliana Martins',
      priority: 'critical',
      dueDate: '08/12/2025',
      status: 'in_progress',
      completionPercentage: 85,
      slaHours: 4,
      description: 'Validar a integridade dos dados sincronizados entre ERP e CRM.',
    },
    {
      id: 'TSK-008',
      title: 'Preparar Apresentação Executiva',
      processName: 'Comunicação Corporativa',
      assignee: 'Ricardo Pereira',
      priority: 'high',
      dueDate: '11/12/2025',
      status: 'pending',
      completionPercentage: 0,
      slaHours: 60,
      description: 'Preparar slides para apresentação executiva sobre resultados do trimestre.',
    },
    {
      id: 'TSK-009',
      title: 'Auditar Processos de Compliance',
      processName: 'Auditoria Interna',
      assignee: 'Beatriz Rocha',
      priority: 'medium',
      dueDate: '18/12/2025',
      status: 'delegated',
      completionPercentage: 30,
      slaHours: 180,
      description: 'Realizar auditoria dos processos de compliance para certificação ISO.',
    },
    {
      id: 'TSK-010',
      title: 'Treinar Novos Usuários',
      processName: 'Capacitação de Usuários',
      assignee: 'Gabriel Santos',
      priority: 'low',
      dueDate: '22/12/2025',
      status: 'pending',
      completionPercentage: 0,
      slaHours: 300,
      description: 'Conduzir sessão de treinamento para novos usuários do sistema BPM.',
    },
    {
      id: 'TSK-011',
      title: 'Analisar Métricas de Performance',
      processName: 'Analytics e Relatórios',
      assignee: 'Camila Dias',
      priority: 'medium',
      dueDate: '14/12/2025',
      status: 'in_progress',
      completionPercentage: 50,
      slaHours: 96,
      description: 'Analisar as métricas de performance dos processos automatizados.',
    },
    {
      id: 'TSK-012',
      title: 'Resolver Incidente Crítico',
      processName: 'Gestão de Incidentes',
      assignee: 'Thiago Barbosa',
      priority: 'critical',
      dueDate: '08/12/2025',
      status: 'in_progress',
      completionPercentage: 90,
      slaHours: 1,
      description: 'Resolver incidente crítico reportado no sistema de produção.',
    },
    {
      id: 'TSK-013',
      title: 'Atualizar Políticas de Segurança',
      processName: 'Segurança da Informação',
      assignee: 'Patricia Lima',
      priority: 'high',
      dueDate: '13/12/2025',
      status: 'pending',
      completionPercentage: 0,
      slaHours: 84,
      description: 'Revisar e atualizar as políticas de segurança da informação.',
    },
    {
      id: 'TSK-014',
      title: 'Migrar Dados Legados',
      processName: 'Migração de Dados',
      assignee: 'Rodrigo Mendes',
      priority: 'medium',
      dueDate: '16/12/2025',
      status: 'in_progress',
      completionPercentage: 40,
      slaHours: 144,
      description: 'Migrar dados do sistema legado para a nova plataforma BPM.',
    },
    {
      id: 'TSK-015',
      title: 'Configurar Backup Automático',
      processName: 'Infraestrutura TI',
      assignee: 'Marcos Vieira',
      priority: 'high',
      dueDate: '10/12/2025',
      status: 'completed',
      completionPercentage: 100,
      slaHours: 48,
      description: 'Configurar rotina de backup automático para banco de dados.',
    },
  ];

  const [tasks, setTasks] = useState<Task[]>(mockTasks);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:254:6" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="254" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:255:8" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="255" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-surface%20border-b%20border-border%22%7D" className="h-16 bg-surface border-b border-border"></div>
        <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:256:8" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="256" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%22%7D" className="flex">
          <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:257:10" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="257" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-72%20bg-surface%20border-r%20border-border%20h-screen%22%7D" className="w-72 bg-surface border-r border-border h-screen"></div>
          <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:258:10" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="258" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20p-6%22%7D" className="flex-1 p-6">
            <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:259:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="259" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-64%20mb-6%22%7D" className="h-8 bg-muted rounded w-64 mb-6"></div>
            <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:260:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="260" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:262:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="262" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-muted%20rounded%22%7D" key={i} className="h-16 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
  };

  const handleTaskSelect = (taskId: string) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map((t) => t.id));
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleQuickAction = (taskId: string, action: string) => {
    console.log(`Quick action: ${action} on task ${taskId}`);
  };

  const handleBulkAssign = () => {
    console.log('Bulk assign:', selectedTasks);
  };

  const handleBulkUpdateStatus = () => {
    console.log('Bulk update status:', selectedTasks);
  };

  const handleBulkDelegate = () => {
    console.log('Bulk delegate:', selectedTasks);
  };

  const handleBulkExport = () => {
    console.log('Bulk export:', selectedTasks);
  };

  const handleClearSelection = () => {
    setSelectedTasks([]);
  };

  const handleSaveTask = (updatedTask: Task) => {
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
  };

  return (
    <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:328:4" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="328" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Header data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:329:6" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="329" data-component-file="TaskManagementInteractive.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <Sidebar data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:330:6" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="330" data-component-file="TaskManagementInteractive.tsx" data-component-name="Sidebar" data-component-content="%7B%22elementName%22%3A%22Sidebar%22%7D" isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:332:6" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="332" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Apl-72%20pt-16%22%7D" className="lg:pl-72 pt-16">
        <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:333:8" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="333" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%22%7D" className="flex">
          <TaskFilters data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:334:10" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="334" data-component-file="TaskManagementInteractive.tsx" data-component-name="TaskFilters" data-component-content="%7B%22elementName%22%3A%22TaskFilters%22%7D"
            filters={filters}
            onFilterChange={handleFilterChange}
            isCollapsed={isFilterCollapsed}
            onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
          />

          <main data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:341:10" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="341" data-component-file="TaskManagementInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22flex-1%20p-6%22%7D" className="flex-1 p-6">
            <Breadcrumb data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:342:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="342" data-component-file="TaskManagementInteractive.tsx" data-component-name="Breadcrumb" data-component-content="%7B%22elementName%22%3A%22Breadcrumb%22%7D" />

            <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:344:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="344" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
              <h1 data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:345:14" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="345" data-component-file="TaskManagementInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Central%20de%20Gerenciamento%20de%20Tarefas%22%7D" className="text-3xl font-bold text-foreground mb-2">Central de Gerenciamento de Tarefas</h1>
              <p data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:346:14" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="346" data-component-file="TaskManagementInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Gerencie%20e%20acompanhe%20todas%20as%20suas%20tarefas%20e%20atribui%C3%A7%C3%B5es%20em%20um%20%C3%BAnico%20lugar%22%7D" className="text-muted-foreground">
                Gerencie e acompanhe todas as suas tarefas e atribuições em um único lugar
              </p>
            </div>

            <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:351:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="351" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20flex%20items-center%20justify-between%20gap-4%22%7D" className="mb-6 flex items-center justify-between gap-4">
              <form data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:352:14" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="352" data-component-file="TaskManagementInteractive.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22flex-1%20max-w-md%22%7D" onSubmit={handleSearch} className="flex-1 max-w-md">
                <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:353:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="353" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
                  <Icon data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:354:18" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="354" data-component-file="TaskManagementInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
                    name="MagnifyingGlassIcon"
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:359:18" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="359" data-component-file="TaskManagementInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-10%20pr-4%20py-2%20bg-surface%20border%20border-border%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar tarefas por título, ID ou processo..."
                    className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </form>
              <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:368:14" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="368" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:369:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="369" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20px-4%20py-2%20bg-surface%20border%20border-border%20text-foreground%20rounded-md%20hover%3Abg-muted%20transition-micro%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Filtros%20Avan%C3%A7ados%22%7D" className="flex items-center gap-2 px-4 py-2 bg-surface border border-border text-foreground rounded-md hover:bg-muted transition-micro text-sm font-medium">
                  <Icon data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:370:18" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="370" data-component-file="TaskManagementInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%7D" name="FunnelIcon" size={16} />
                  Filtros Avançados
                </button>
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:373:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="373" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20px-4%20py-2%20bg-surface%20border%20border-border%20text-foreground%20rounded-md%20hover%3Abg-muted%20transition-micro%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Exportar%22%7D" className="flex items-center gap-2 px-4 py-2 bg-surface border border-border text-foreground rounded-md hover:bg-muted transition-micro text-sm font-medium">
                  <Icon data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:374:18" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="374" data-component-file="TaskManagementInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowDownTrayIcon%22%7D" name="ArrowDownTrayIcon" size={16} />
                  Exportar
                </button>
              </div>
            </div>

            <BulkActionToolbar data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:380:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="380" data-component-file="TaskManagementInteractive.tsx" data-component-name="BulkActionToolbar" data-component-content="%7B%22elementName%22%3A%22BulkActionToolbar%22%7D"
              selectedCount={selectedTasks.length}
              onAssign={handleBulkAssign}
              onUpdateStatus={handleBulkUpdateStatus}
              onDelegate={handleBulkDelegate}
              onExport={handleBulkExport}
              onClearSelection={handleClearSelection}
            />

            <TaskTable data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:389:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="389" data-component-file="TaskManagementInteractive.tsx" data-component-name="TaskTable" data-component-content="%7B%22elementName%22%3A%22TaskTable%22%7D"
              tasks={tasks}
              selectedTasks={selectedTasks}
              onTaskSelect={handleTaskSelect}
              onSelectAll={handleSelectAll}
              onTaskClick={handleTaskClick}
              onQuickAction={handleQuickAction}
            />

            <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:398:12" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="398" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20flex%20items-center%20justify-between%22%7D" className="mt-6 flex items-center justify-between">
              <p data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:399:14" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="399" data-component-file="TaskManagementInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Mostrando%20de%20tarefas%22%7D" className="text-sm text-muted-foreground">
                Mostrando {tasks.length} de {tasks.length} tarefas
              </p>
              <div data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:402:14" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="402" data-component-file="TaskManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:403:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="403" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20py-1%20bg-surface%20border%20border-border%20text-foreground%20rounded-md%20hover%3Abg-muted%20transition-micro%20text-sm%20disabled%3Aopacity-50%22%2C%22textContent%22%3A%22Anterior%22%7D" className="px-3 py-1 bg-surface border border-border text-foreground rounded-md hover:bg-muted transition-micro text-sm disabled:opacity-50">
                  Anterior
                </button>
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:406:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="406" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20py-1%20bg-primary%20text-primary-foreground%20rounded-md%20text-sm%22%2C%22textContent%22%3A%221%22%7D" className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-sm">
                  1
                </button>
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:409:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="409" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20py-1%20bg-surface%20border%20border-border%20text-foreground%20rounded-md%20hover%3Abg-muted%20transition-micro%20text-sm%22%2C%22textContent%22%3A%222%22%7D" className="px-3 py-1 bg-surface border border-border text-foreground rounded-md hover:bg-muted transition-micro text-sm">
                  2
                </button>
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:412:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="412" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20py-1%20bg-surface%20border%20border-border%20text-foreground%20rounded-md%20hover%3Abg-muted%20transition-micro%20text-sm%22%2C%22textContent%22%3A%223%22%7D" className="px-3 py-1 bg-surface border border-border text-foreground rounded-md hover:bg-muted transition-micro text-sm">
                  3
                </button>
                <button data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:415:16" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="415" data-component-file="TaskManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20py-1%20bg-surface%20border%20border-border%20text-foreground%20rounded-md%20hover%3Abg-muted%20transition-micro%20text-sm%22%2C%22textContent%22%3A%22Pr%C3%B3ximo%22%7D" className="px-3 py-1 bg-surface border border-border text-foreground rounded-md hover:bg-muted transition-micro text-sm">
                  Próximo
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>

      <TaskDetailsModal data-component-id="src/app/task-management-center/components/TaskManagementInteractive.tsx:424:6" data-component-path="src/app/task-management-center/components/TaskManagementInteractive.tsx" data-component-line="424" data-component-file="TaskManagementInteractive.tsx" data-component-name="TaskDetailsModal" data-component-content="%7B%22elementName%22%3A%22TaskDetailsModal%22%7D"
        task={selectedTask}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTask}
      />
    </div>
  );
};

export default TaskManagementInteractive;